import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useToast } from "vue-toastification";
import api from "../services/api";

export const useAuthStore = defineStore("auth", () => {
  const toast = useToast();

  // State
  const token = ref(localStorage.getItem("token"));
  const userProfile = ref(null);
  const isLoading = ref(false);

  // Getters
  const isAuthenticated = computed(() => !!token.value);
  const onboardingProgress = computed(
    () => userProfile.value?.onboardingProgress || 0
  );

  // Actions
  const setToken = (newToken) => {
    token.value = newToken;
    if (newToken) {
      localStorage.setItem("token", newToken);
    } else {
      localStorage.removeItem("token");
    }
  };

  const setUserProfile = (profile) => {
    userProfile.value = profile;
  };

  const login = async (credentials) => {
    try {
      isLoading.value = true;
      const response = await api.post("/auth/login", credentials);

      setToken(response.data.token);
      setUserProfile(response.data.influencer);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || "Login failed";
      toast.error(message);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const register = async (userData) => {
    try {
      isLoading.value = true;
      const response = await api.post("/auth/register", userData);

      setToken(response.data.token);
      setUserProfile(response.data.influencer);

      toast.success("Registration successful!");
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || "Registration failed";
      toast.error(message);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const socialLogin = async (providerData) => {
    try {
      isLoading.value = true;
      const response = await api.post("/auth/social-login", providerData);

      setToken(response.data.token);
      setUserProfile(response.data.influencer);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || "Social login failed";
      toast.error(message);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchProfile = async () => {
    try {
      if (!token.value) return null;

      const response = await api.get("/auth/me");
      setUserProfile(response.data.influencer);
      return response.data.influencer;
    } catch (error) {
      // If token is invalid, logout
      if (error.response?.status === 401) {
        logout();
      }
      throw error;
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put("/auth/profile", profileData);
      setUserProfile(response.data.influencer);
      toast.success("Profile updated successfully!");
      return response.data.influencer;
    } catch (error) {
      const message = error.response?.data?.error || "Failed to update profile";
      toast.error(message);
      throw error;
    }
  };

  const logout = () => {
    setToken(null);
    setUserProfile(null);
    toast.info("Logged out successfully");
  };

  // Initialize store
  const initialize = async () => {
    if (token.value) {
      try {
        await fetchProfile();
      } catch (error) {
        console.error("Failed to fetch profile on initialization:", error);
        logout();
      }
    }
  };

  return {
    // State
    token,
    userProfile,
    isLoading,

    // Getters
    isAuthenticated,
    onboardingProgress,

    // Actions
    login,
    register,
    socialLogin,
    fetchProfile,
    updateProfile,
    logout,
    initialize,
  };
});
